import fetcher from '@/core/lib/fetcher'
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { APIError, APIResponse } from '@/core/types/utils'
import { toast } from '../ui/use-toast'
import { parseApiErrorMessage } from '@/core/lib/utils'
import {
  AllocatedAccount,
  CommissionFormValues,
  Disbursement,
  DisbursementStatus,
  FeeAudit,
  FeeAuditFeeType,
  FeeAuditScopeType,
  GroupExtraFee,
  HotelPayoutAccount,
  PayoutProvider,
  PayoutScope,
  PlatformFeeFormValues,
  PlatformMasterAccount,
  PlatformMasterAccountDetail,
  PlatformMasterAccountPatch,
  ScopePlatformAccount,
  ScopePlatformFee,
  ProcessingFeeFormValues,
  PropertyExtraFee,
  PropertyProcessingFee,
} from './commissions.types'

const BASE = import.meta.env.VITE_API_BOOKING as string

export const allocatedAccountsQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['allocatedAccounts', token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/allocated/accounts`)
        .json<APIResponse<AllocatedAccount[]>>(),
    retry: false,
    refetchOnWindowFocus: false,
  })

export const groupExtraFeesQueryOptions = (token: string, groupId: string) =>
  queryOptions({
    queryKey: ['groupExtraFees', groupId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/groups/${groupId}/extra-fees`)
        .json<APIResponse<GroupExtraFee[]>>(),
    enabled: !!groupId,
    retry: false,
  })

export const useCreateGroupExtraFee = (token: string, groupId: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (data: CommissionFormValues) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/groups/${groupId}/extra-fees`)
        .post(data)
        .json<APIResponse<GroupExtraFee>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['groupExtraFees', groupId] })
      toast({ title: intl.formatMessage({ id: 'commissions.toastCreated' }) })
    },
    onError: (error) => {
      const e: APIError<null> = JSON.parse((error as Error).message)
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: e.message,
        variant: 'destructive',
      })
    },
  })
}

export const useUpdateGroupExtraFee = (
  token: string,
  groupId: string,
  feeId: string
) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: ({
      feeType,
      feeValue,
      fixedFeeValue,
      label,
      status,
    }: Partial<CommissionFormValues>) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/groups/${groupId}/extra-fees/${feeId}`)
        .patch({ feeType, feeValue, fixedFeeValue, label, status })
        .json<APIResponse<GroupExtraFee>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['groupExtraFees', groupId] })
      toast({ title: intl.formatMessage({ id: 'commissions.toastUpdated' }) })
    },
    onError: (error) => {
      const e: APIError<null> = JSON.parse((error as Error).message)
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: e.message,
        variant: 'destructive',
      })
    },
  })
}

export const useDeleteGroupExtraFee = (token: string, groupId: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (feeId: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/groups/${groupId}/extra-fees/${feeId}`)
        .delete()
        .json<APIResponse<{ id: string }>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['groupExtraFees', groupId] })
      toast({ title: intl.formatMessage({ id: 'commissions.toastRemoved' }) })
    },
    onError: (error) => {
      const e: APIError<null> = JSON.parse((error as Error).message)
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: e.message,
        variant: 'destructive',
      })
    },
  })
}

export const propertyExtraFeesQueryOptions = (token: string, propertyId: string) =>
  queryOptions({
    queryKey: ['propertyExtraFees', propertyId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/properties/${propertyId}/extra-fees`)
        .json<APIResponse<PropertyExtraFee[]>>(),
    enabled: !!propertyId,
    retry: false,
  })

export const useCreatePropertyExtraFee = (token: string, propertyId: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (data: CommissionFormValues) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/properties/${propertyId}/extra-fees`)
        .post(data)
        .json<APIResponse<PropertyExtraFee>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['propertyExtraFees', propertyId] })
      toast({ title: intl.formatMessage({ id: 'commissions.toastCreated' }) })
    },
    onError: (error) => {
      const e: APIError<null> = JSON.parse((error as Error).message)
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: e.message,
        variant: 'destructive',
      })
    },
  })
}

export const useUpdatePropertyExtraFee = (
  token: string,
  propertyId: string,
  feeId: string
) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: ({
      feeType,
      feeValue,
      fixedFeeValue,
      label,
      status,
    }: Partial<CommissionFormValues>) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/properties/${propertyId}/extra-fees/${feeId}`)
        .patch({ feeType, feeValue, fixedFeeValue, label, status })
        .json<APIResponse<PropertyExtraFee>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['propertyExtraFees', propertyId] })
      toast({ title: intl.formatMessage({ id: 'commissions.toastUpdated' }) })
    },
    onError: (error) => {
      const e: APIError<null> = JSON.parse((error as Error).message)
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: e.message,
        variant: 'destructive',
      })
    },
  })
}

export const useDeletePropertyExtraFee = (token: string, propertyId: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (feeId: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/properties/${propertyId}/extra-fees/${feeId}`)
        .delete()
        .json<APIResponse<{ id: string }>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['propertyExtraFees', propertyId] })
      toast({ title: intl.formatMessage({ id: 'commissions.toastRemoved' }) })
    },
    onError: (error) => {
      const e: APIError<null> = JSON.parse((error as Error).message)
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: e.message,
        variant: 'destructive',
      })
    },
  })
}

export const propertyProcessingFeesQueryOptions = (token: string, propertyId: string) =>
  queryOptions({
    queryKey: ['propertyProcessingFees', propertyId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/properties/${propertyId}/processing-fees`)
        .json<APIResponse<PropertyProcessingFee[]>>(),
    enabled: !!propertyId,
    retry: false,
    refetchOnWindowFocus: false,
  })

export const useCreatePropertyProcessingFee = (token: string, propertyId: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (data: ProcessingFeeFormValues) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/properties/${propertyId}/processing-fees`)
        .post(data)
        .json<APIResponse<PropertyProcessingFee>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['propertyProcessingFees', propertyId] })
      toast({
        title: intl.formatMessage({ id: 'commissions.toastProcessingCreated' }),
      })
    },
    onError: (error) => {
      const e: APIError<null> = JSON.parse((error as Error).message)
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: e.message,
        variant: 'destructive',
      })
    },
  })
}

export const useUpdatePropertyProcessingFee = (
  token: string,
  propertyId: string,
  feeId: string
) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: ({ processingFeeType, processingFeeValue, fixedFeeValue, status }: ProcessingFeeFormValues) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/properties/${propertyId}/processing-fees/${feeId}`)
        .patch({ processingFeeType, processingFeeValue, fixedFeeValue, status })
        .json<APIResponse<PropertyProcessingFee>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['propertyProcessingFees', propertyId] })
      toast({
        title: intl.formatMessage({ id: 'commissions.toastProcessingUpdated' }),
      })
    },
    onError: (error) => {
      const e: APIError<null> = JSON.parse((error as Error).message)
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: e.message,
        variant: 'destructive',
      })
    },
  })
}

export const useDeletePropertyProcessingFee = (token: string, propertyId: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (feeId: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/properties/${propertyId}/processing-fees/${feeId}`)
        .delete()
        .json<APIResponse<{ id: string }>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['propertyProcessingFees', propertyId] })
      toast({
        title: intl.formatMessage({ id: 'commissions.toastProcessingRemoved' }),
      })
    },
    onError: (error) => {
      const e: APIError<null> = JSON.parse((error as Error).message)
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: e.message,
        variant: 'destructive',
      })
    },
  })
}

export const disbursementsQueryOptions = (
  token: string,
  params: {
    propertyId?: string
    groupId?: string
    bookingId?: string
    status?: DisbursementStatus
    limit?: number
  } = {}
) =>
  queryOptions({
    queryKey: ['disbursements', params, token],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (params.propertyId) qs.set('propertyId', params.propertyId)
      if (params.groupId) qs.set('groupId', params.groupId)
      if (params.bookingId) qs.set('bookingId', params.bookingId)
      if (params.status) qs.set('status', params.status)
      if (params.limit) qs.set('limit', String(params.limit))
      const qsStr = qs.toString()
      return fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/disbursements${qsStr ? `?${qsStr}` : ''}`)
        .json<APIResponse<Disbursement[]>>()
    },
    retry: false,
    refetchOnWindowFocus: false,
  })

export const feeAuditsQueryOptions = (
  token: string,
  params: {
    scopeType?: FeeAuditScopeType
    scopeId?: string
    feeType?: FeeAuditFeeType
    limit?: number
  } = {}
) =>
  queryOptions({
    queryKey: ['feeAudits', params, token],
    queryFn: () => {
      const qs = new URLSearchParams()
      if (params.scopeType) qs.set('scopeType', params.scopeType)
      if (params.scopeId) qs.set('scopeId', params.scopeId)
      if (params.feeType) qs.set('feeType', params.feeType)
      if (params.limit) qs.set('limit', String(params.limit))
      const qsStr = qs.toString()
      return fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/fee-audits${qsStr ? `?${qsStr}` : ''}`)
        .json<APIResponse<FeeAudit[]>>()
    },
    enabled: !!params.scopeId,
    retry: false,
    refetchOnWindowFocus: false,
  })

// ─── Commission per payout scope (property-service) ─────────────────────────

/** `data: null` means "not configured"; an error is always an error (A1). */
export const scopePlatformFeeQueryOptions = (token: string, scope: PayoutScope | null) =>
  queryOptions({
    queryKey: ['scopePlatformFee', scope?.scopeType, scope?.scopeId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}property/fees/${scope?.scopeType}/${scope?.scopeId}`)
        .json<APIResponse<ScopePlatformFee | null>>(),
    enabled: !!scope?.scopeId,
    retry: false,
    refetchOnWindowFocus: false,
  })

export const useUpsertScopePlatformFee = (token: string, scope: PayoutScope | null) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (data: PlatformFeeFormValues) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}property/fees/${scope?.scopeType}/${scope?.scopeId}`)
        .put(data)
        .json<APIResponse<ScopePlatformFee>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['scopePlatformFee', scope?.scopeType, scope?.scopeId],
      })
      toast({ title: intl.formatMessage({ id: 'commissions.toastPlatformSaved' }) })
    },
    onError: (error) => {
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: parseApiErrorMessage(error),
        variant: 'destructive',
      })
    },
  })
}

// ─── Payout providers, hotel accounts and Albie platform accounts (payment-service) ─────

export const payoutProvidersQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['payoutProviders', token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/providers`)
        .json<APIResponse<PayoutProvider[]>>(),
    retry: false,
    refetchOnWindowFocus: false,
  })

/** The property's hotel account; `data: null` when it has none yet. */
export const hotelAccountQueryOptions = (token: string, propertyId: string) =>
  queryOptions({
    queryKey: ['hotelAccount', propertyId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/resources/${propertyId}/account`)
        .json<APIResponse<HotelPayoutAccount | null>>(),
    enabled: !!propertyId,
    retry: false,
    refetchOnWindowFocus: false,
  })

/** Callers handle errors themselves (field errors go under their inputs), so no toast here. */
export const useCreateHotelAccount = (token: string, propertyId: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/resources/${propertyId}/account`)
        .post(body)
        .json<APIResponse<HotelPayoutAccount>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hotelAccount', propertyId] })
      toast({ title: intl.formatMessage({ id: 'commissions.toastHotelAccountCreated' }) })
    },
  })
}

export const platformMasterAccountsQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['platformMasterAccounts', token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/platform-master-accounts?limit=200`)
        .json<APIResponse<PlatformMasterAccount[]>>(),
    retry: false,
    refetchOnWindowFocus: false,
  })

export const platformMasterAccountQueryOptions = (token: string, masterAccountId: string) =>
  queryOptions({
    queryKey: ['platformMasterAccount', masterAccountId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}payouts/platform-master-accounts/${masterAccountId}`)
        .json<APIResponse<PlatformMasterAccountDetail>>(),
    enabled: !!masterAccountId,
    retry: false,
    refetchOnWindowFocus: false,
  })

/** Callers handle errors themselves (field errors go under their inputs), so no toast here. */
export const useCreatePlatformMasterAccount = (token: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/platform-master-accounts`)
        .post(body)
        .json<APIResponse<PlatformMasterAccount>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platformMasterAccounts'] })
      toast({ title: intl.formatMessage({ id: 'commissions.toastMasterCreated' }) })
    },
  })
}

/** Callers handle errors themselves (a requirement error goes back under its input), so no error toast here. */
export const usePatchPlatformMasterAccount = (token: string) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: ({
      masterAccountId,
      patch,
    }: {
      masterAccountId: string
      patch: PlatformMasterAccountPatch
    }) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}payouts/platform-master-accounts/${masterAccountId}`)
        .patch(patch)
        .json<APIResponse<PlatformMasterAccount & { childrenUpdated: number }>>(),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['platformMasterAccounts'] })
      void queryClient.invalidateQueries({ queryKey: ['platformMasterAccount'] })
      void queryClient.invalidateQueries({ queryKey: ['scopePlatformAccounts'] })
      toast({
        title: intl.formatMessage(
          { id: 'commissions.toastMasterUpdated' },
          { count: response.data.childrenUpdated }
        ),
      })
    },
  })
}

const scopePath = (scope: PayoutScope) =>
  scope.scopeType === 'group'
    ? `payouts/groups/${scope.scopeId}/platform-accounts`
    : `payouts/properties/${scope.scopeId}/platform-accounts`

export const scopePlatformAccountsQueryOptions = (token: string, scope: PayoutScope | null) =>
  queryOptions({
    queryKey: ['scopePlatformAccounts', scope?.scopeType, scope?.scopeId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(`${BASE}${scopePath(scope as PayoutScope)}`)
        .json<APIResponse<ScopePlatformAccount[]>>(),
    enabled: !!scope?.scopeId,
    retry: false,
    refetchOnWindowFocus: false,
  })

/** Assigning a master always creates the scope's own child account. */
export const useAssignPlatformMasterAccount = (token: string, scope: PayoutScope | null) => {
  const queryClient = useQueryClient()
  const intl = useIntl()
  return useMutation({
    mutationFn: (masterAccountId: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(`${BASE}${scopePath(scope as PayoutScope)}`)
        .post({ masterAccountId })
        .json<APIResponse<ScopePlatformAccount>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['scopePlatformAccounts', scope?.scopeType, scope?.scopeId],
      })
      void queryClient.invalidateQueries({ queryKey: ['platformMasterAccounts'] })
      toast({ title: intl.formatMessage({ id: 'commissions.toastMasterAssigned' }) })
    },
    onError: (error) => {
      toast({
        title: intl.formatMessage({ id: 'common.error' }),
        description: parseApiErrorMessage(error),
        variant: 'destructive',
      })
    },
  })
}
